/**
 * Shop Nearby Dispensaries Component
 *
 * Purpose: Displays nearby dispensary locations based on the current selected store
 * Used on: Category pages, potentially other pages
 *
 * Key Features:
 * - Shows 4 closest dispensary locations (excluding current store)
 * - Responsive grid layout (1 mobile, 2 tablet, 3-4 desktop)
 * - Uses WordPress location data with fallback images
 * - Integrates with store context for current store detection
 * - Maps WordPress slugs to store values for proper routing
 */

import { Link } from '@remix-run/react';
import { useStore } from '~/context/store-context';
import { getStoreHoursOverride } from '~/utils/store-hours-override';

// Store interface matching the locations page
interface Store {
  name: string;
  value: string;
  address: string;
  hours: string;
  url: string;
  phone?: string;
}

// WordPress location interface - matching the locations page
interface WordPressLocation {
  title: string;
  slug: string;
  locationDetails: {
    storeImage?: {
      node?: {
        mediaItemUrl: string;
        altText: string;
      };
    };
    address: string;
    addressmap?: {
      latitude: number;
      longitude: number;
    };
    openingHours: string;
    phone: string;
    email: string;
    comingSoon?: string[];
  };
}

interface ShopNearbyDispensariesProps {
  currentStore?: string;
  allLocations?: WordPressLocation[];
  className?: string;
  onNavigate?: () => void;
}

// Store data - matches the locations page
const stores: Store[] = [
  {
    name: 'ANTIOCH',
    value: 'catalystantioch',
    address: '2625 Crow Ct, Antioch, CA 94509',
    url: '/dispensary-locations/antioch-dispensary',
    hours: 'Open 8am - 8pm',
    phone: '(925) 756-7140',
  },
  {
    name: 'BELLFLOWER',
    value: 'catalystbellflower',
    address: '9032 Artesia Blvd, Bellflower, CA 90706',
    url: '/dispensary-locations/bellflower-dispensary',
    hours: 'Open 9am - 9pm',
    phone: '(562) 925-5323',
  },
  {
    name: 'BELMONT SHORE',
    value: 'catalystbelmont',
    address: '5227 E 2nd St, Long Beach, CA 90803',
    url: '/dispensary-locations/belmont-shore-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(562) 439-0420',
  },
  {
    name: 'CHERRY',
    value: 'catalyst405',
    address: '3170 Cherry Ave, Long Beach, CA 90807',
    url: '/dispensary-locations/cherry-ave-long-beach-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(562) 427-5323',
  },
  {
    name: 'COSTA MESA',
    value: 'catalystcostamesa',
    address: '170 E 17th St Ste 115, Costa Mesa, CA 92627',
    url: '/dispensary-locations/costa-mesa-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(714) 754-4200',
  },
  {
    name: 'DALY CITY',
    value: 'catalystdalycity',
    address: '1000 King Dr Ste 200, Daly City, CA 94015',
    url: '/dispensary-locations/daly-city-dispensary',
    hours: 'Open 10am - 9.30pm',
    phone: '(650) 301-4420',
  },
  {
    name: 'DOWNTOWN LONG BEACH',
    value: 'catalystpine',
    address: '433 Pine Street, Ste 500, Long Beach, CA 90802',
    url: '/dispensary-locations/downtown-long-beach-dispensary',
    hours: 'Open 10.30am - 9.30pm',
    phone: '(562) 612-4420',
  },
  {
    name: 'EL MONTE',
    value: 'catalystelmonte',
    address: '12154 Valley Blvd, El Monte, CA 91732',
    url: '/dispensary-locations/el-monte-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(626) 350-4200',
  },
  {
    name: 'FIGUEROA',
    value: 'catalystfigueroa',
    address: '10020 S Figueroa St, Los Angeles, CA 90003',
    url: '/dispensary-locations/figueroa-los-angeles-dispensary',
    hours: 'Open 10am - 9.30pm',
    phone: '(323) 305-3401',
  },
  {
    name: 'HUNTINGTON BEACH',
    value: 'catalysthuntington',
    address: '7631 Center Ave, Huntington Beach, CA 92647',
    url: '/dispensary-locations/huntington-beach-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(714) 465-4200',
  },
  {
    name: 'LONG BEACH',
    value: 'catalystlongbeach',
    address: '3500 Long Beach Blvd, Long Beach, CA 90807',
    url: '/dispensary-locations/long-beach-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(562) 426-4200',
  },
  {
    name: 'OXNARD',
    value: 'catalystoxnard',
    address: '1835 N Ventura Rd, Oxnard, CA 93036',
    url: '/dispensary-locations/oxnard-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(805) 983-4200',
  },
  {
    name: 'RETRO ROW LONG BEACH',
    value: 'catalystlift',
    address: '2142 E 4th St, Long Beach, CA 90814',
    url: '/dispensary-locations/retro-row-long-beach-dispensary',
    hours: 'Open 10am - 9.30pm',
    phone: '(562) 433-4200',
  },
  {
    name: 'PLANET CATALYST',
    value: 'catalystp13',
    address: '3400 W Warner Ave, Santa Ana, CA 92704',
    url: '/dispensary-locations/planet-catalyst-orange-county',
    hours: 'Mon-Thu 9am - 9.30pm | Fri-Sun 8am - 9.30pm',
    phone: '(562) 732-1313',
  },
  {
    name: 'SANTA ANA',
    value: 'catalystsantaana',
    address: '1835 S Grand Ave, Santa Ana, CA 92705',
    url: '/dispensary-locations/santa-ana-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(714) 542-4200',
  },
  {
    name: 'TORRANCE',
    value: 'catalysttorrance',
    address: '19800 Hawthorne Blvd, Torrance, CA 90503',
    url: '/dispensary-locations/torrance-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(310) 370-4200',
  },
  {
    name: 'WEST HOLLYWOOD',
    value: 'catalystweho',
    address: '8923 Santa Monica Blvd, West Hollywood, CA 90069',
    url: '/dispensary-locations/west-hollywood-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(310) 652-4200',
  },
];

// Apply date-based hour overrides (e.g., 4/20 weekend, holidays)
const storesWithOverrides: Store[] = stores.map((store) => {
  const override = getStoreHoursOverride(store.value);
  if (override) {
    return { ...store, hours: override.hours };
  }
  return store;
});

// Default fallback images - using actual available images
const defaultImages = [
  '/images/catalyst-antioch-cannabis-dispensary-interior-display-cases-5851-1-scaled.webp',
  '/images/Catalyst-Cannabis-Dispensary-Store-Hours.webp',
  '/images/catalyst-antioch-cannabis-dispensary-interior-display-cases-5851-1-scaled.webp',
  '/images/Catalyst-Cannabis-Dispensary-Store-Hours.webp',
];

// Helper functions - ensure consistent fallback images
const getDispensaryImage = (index: number): string => {
  // Always use the first fallback image to prevent hydration mismatches
  return defaultImages[0];
};

const createStoreValueMap = () => {
  const storeValueMap: Record<string, string> = {};

  storesWithOverrides.forEach((store) => {
    const urlParts = store.url.split('/').filter(Boolean);
    const slug = urlParts[urlParts.length - 1];
    if (slug) {
      storeValueMap[slug] = store.value;
      // Also add version without '-dispensary' suffix for fallback
      const slugWithoutSuffix = slug.replace('-dispensary', '');
      storeValueMap[slugWithoutSuffix] = store.value;
    }
  });

  return storeValueMap;
};

// Haversine formula to calculate distance between two coordinates
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return distance;
};

// Get closest WordPress locations based on current store
const getClosestStores = (
  currentStore: string,
  allLocations: WordPressLocation[],
  limit: number = 3,
): WordPressLocation[] => {
  if (!allLocations || allLocations.length === 0) {
    return [];
  }

  // Filter out coming soon locations and find current store location
  const availableLocations = allLocations.filter(
    (loc) => !loc.locationDetails?.comingSoon?.includes('Yes'),
  );

  // Find current store's WordPress location for distance calculation
  const storeValueMap = createStoreValueMap();
  const currentStoreSlug = Object.keys(storeValueMap).find(
    (slug) => storeValueMap[slug] === currentStore,
  );

  const currentLocation = availableLocations.find((loc) => loc.slug === currentStoreSlug);

  if (!currentLocation?.locationDetails?.addressmap) {
    // If we can't find current location, just return first 4 available locations
    return availableLocations.slice(0, limit);
  }

  const currentLat = currentLocation.locationDetails.addressmap.latitude;
  const currentLon = currentLocation.locationDetails.addressmap.longitude;

  // Calculate distances and sort by distance
  const locationsWithDistance = availableLocations
    .filter((loc) => loc.slug !== currentStoreSlug) // Exclude current store
    .map((loc) => {
      const distance = loc.locationDetails?.addressmap
        ? calculateDistance(
            currentLat,
            currentLon,
            loc.locationDetails.addressmap.latitude,
            loc.locationDetails.addressmap.longitude,
          )
        : 999; // Large distance for locations without coordinates

      return { ...loc, distance };
    })
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit);

  return locationsWithDistance;
};

// Get nearby stores (fallback to hardcoded stores if no WordPress data)
const getNearbyStores = (currentStore: string, limit: number = 3): Store[] => {
  // Filter out the current store and get first 3
  return storesWithOverrides.filter((store) => store.value !== currentStore).slice(0, limit);
};

export default function ShopNearbyDispensaries({
  currentStore,
  allLocations,
  className = '',
  onNavigate,
}: ShopNearbyDispensariesProps) {
  const { selectedStore } = useStore();

  // Use provided store or fall back to context
  const activeStore = currentStore || selectedStore;

  // Get nearby stores - prefer WordPress data if available
  const nearbyWordPressLocations = allLocations ? getClosestStores(activeStore, allLocations) : [];

  const nearbyHardcodedStores =
    nearbyWordPressLocations.length === 0 ? getNearbyStores(activeStore) : [];

  // Use WordPress data if available, otherwise fallback to hardcoded stores
  const hasWordPressData = nearbyWordPressLocations.length > 0;

  if (!hasWordPressData && nearbyHardcodedStores.length === 0) {
    return null;
  }

  return (
    <section id="nearby-dispensaries" className={`w-full bg-[#F8FAFC] ${className}`}>
      <div className="flex flex-col items-center gap-10 px-4 py-4 lg:py-10">
        {/* Header Copy */}
        <div className="flex w-full max-w-[80rem] flex-col items-center gap-0">
          <div className="flex w-full flex-col items-start gap-0">
            <h2 className="font-poppins w-full text-2xl leading-8 font-medium text-[#181818]">
              Shop Nearby Dispensaries
            </h2>
          </div>
        </div>

        {/* Cards - Responsive Grid: 1 mobile, 2 tablet, 3 desktop */}
        <div className="grid w-full max-w-[80rem] grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {hasWordPressData
            ? nearbyWordPressLocations.map((wpLocation, index) => {
                // Get store value for this WordPress location
                const storeValueMap = createStoreValueMap();
                const storeValue = storeValueMap[wpLocation.slug];
                const storeData = storesWithOverrides.find((s) => s.value === storeValue);

                // Use WordPress image if available, otherwise fallback to default
                const imageSrc =
                  wpLocation.locationDetails.storeImage?.node?.mediaItemUrl ||
                  getDispensaryImage(index);
                const imageAltText =
                  wpLocation.locationDetails.storeImage?.node?.altText ||
                  `${wpLocation.title} dispensary`;

                return (
                  <div
                    key={wpLocation.slug}
                    className="flex min-h-[500px] w-full flex-col items-start gap-0 rounded-xl bg-white shadow-[0px_4px_8px_rgba(0,0,0,0.15)]"
                  >
                    {/* Image Container */}
                    <div className="relative h-[200px] w-full rounded-t-xl bg-[#006AA8]">
                      <img
                        src={imageSrc}
                        alt={imageAltText}
                        className="h-[200px] w-full rounded-t-xl object-cover"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex w-full flex-1 flex-col justify-between px-4 py-6">
                      <div className="flex flex-col items-center gap-3">
                        {/* Header */}
                        <div className="flex w-full flex-col items-center gap-2">
                          {/* Location & Stars */}
                          <div className="flex w-full items-center gap-2.5">
                            <div className="flex flex-1 items-center gap-1">
                              <span className="font-helvetica-neue text-xs leading-[18px] font-normal text-gray-600">
                                (5.0)
                              </span>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/locations/cannabis-gold.svg"
                                  alt="Star"
                                  className="h-[18px] w-[17.87px]"
                                />
                                <img
                                  src="/locations/cannabis-gold.svg"
                                  alt="Star"
                                  className="h-[18px] w-[17.87px]"
                                />
                                <img
                                  src="/locations/cannabis-gold.svg"
                                  alt="Star"
                                  className="h-[18px] w-[17.87px]"
                                />
                                <img
                                  src="/locations/cannabis-gold.svg"
                                  alt="Star"
                                  className="h-[18px] w-[17.87px]"
                                />
                                <img
                                  src="/locations/cannabis-gold.svg"
                                  alt="Star"
                                  className="h-[18px] w-[17.87px]"
                                />
                              </div>
                            </div>
                            {/* Distance */}
                            {(wpLocation as any).distance && (
                              <span className="font-helvetica-neue text-xs leading-[18px] font-normal text-[#4A5565]">
                                {(wpLocation as any).distance.toFixed(1)} miles away
                              </span>
                            )}
                          </div>

                          {/* H4 */}
                          <div className="flex w-full items-start gap-2.5">
                            <h3 className="font-helvetica-neue text-start text-2xl leading-tight font-medium text-[#0F172A]">
                              {wpLocation.title}
                            </h3>
                          </div>
                        </div>

                        {/* Location & Hours */}
                        <div className="flex w-full flex-col items-start gap-3">
                          {/* Address */}
                          <div className="flex w-full items-start gap-3 pl-0.5">
                            <img
                              src="/locations/Map.svg"
                              alt="Store location"
                              className="h-6 w-[18px]"
                            />
                            <span className="font-helvetica-neue flex-1 text-lg leading-[22px] font-normal text-[#4A5565]">
                              {wpLocation.locationDetails.address}
                            </span>
                          </div>

                          {/* Hours */}
                          <div className="flex w-full items-center gap-3 pl-0.5">
                            <img
                              src="/locations/Clock.svg"
                              alt="Store hours"
                              className="h-[18px] w-[18px]"
                            />
                            <span className="font-helvetica-neue text-lg leading-[22px] font-normal text-[#4A5565]">
                              {wpLocation.locationDetails.openingHours.replace('Open ', '')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Buttons - Always at bottom */}
                      <div className="flex w-full items-start justify-center gap-4">
                        <Link
                          to={`/dispensary-locations/${wpLocation.slug}`}
                          onClick={onNavigate}
                          aria-label={`Shop Now at ${wpLocation.title}`}
                          className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] bg-[#264B9A] py-3 transition-colors hover:bg-blue-700"
                        >
                          <span className="font-helvetica-neue text-center text-base leading-4 font-medium text-nowrap text-white">
                            Shop Now
                          </span>
                        </Link>
                        <a
                          href={`tel:${wpLocation.locationDetails.phone || '(925) 276-9420'}`}
                          className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] px-6 py-3"
                        >
                          <img src="/locations/phone.svg" alt="Phone contact" className="h-4 w-4" />
                          <span className="font-helvetica-neue text-center text-base leading-4 font-medium text-nowrap text-[#264B9A]">
                            {wpLocation.locationDetails.phone || '(925) 276-9420'}
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })
            : nearbyHardcodedStores.map((store, index) => {
                // Fallback to hardcoded store data
                const imageSrc = getDispensaryImage(index);
                const imageAltText = `${store.name} dispensary`;

                return (
                  <div
                    key={store.value}
                    className="flex min-h-[500px] w-full flex-col items-start gap-0 rounded-xl bg-white shadow-[0px_4px_8px_rgba(0,0,0,0.15)]"
                  >
                    {/* Image Container */}
                    <div className="relative h-[200px] w-full rounded-t-xl bg-[#006AA8]">
                      <img
                        src={imageSrc}
                        alt={imageAltText}
                        className="h-[200px] w-full rounded-t-xl object-cover"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex w-full flex-1 flex-col justify-between px-4 py-6">
                      <div className="flex flex-col items-center gap-3">
                        {/* Header */}
                        <div className="flex w-full flex-col items-center gap-2">
                          {/* Location & Stars */}
                          <div className="flex w-full items-center gap-2.5">
                            <div className="flex flex-1 items-center gap-1">
                              <span className="font-helvetica-neue text-xs leading-[18px] font-normal text-gray-600">
                                (5.0)
                              </span>
                              <div className="flex items-center gap-1">
                                <img
                                  src="/locations/cannabis-gold.svg"
                                  alt="Star"
                                  className="h-[18px] w-[17.87px]"
                                />
                                <img
                                  src="/locations/cannabis-gold.svg"
                                  alt="Star"
                                  className="h-[18px] w-[17.87px]"
                                />
                                <img
                                  src="/locations/cannabis-gold.svg"
                                  alt="Star"
                                  className="h-[18px] w-[17.87px]"
                                />
                                <img
                                  src="/locations/cannabis-gold.svg"
                                  alt="Star"
                                  className="h-[18px] w-[17.87px]"
                                />
                                <img
                                  src="/locations/cannabis-gold.svg"
                                  alt="Star"
                                  className="h-[18px] w-[17.87px]"
                                />
                              </div>
                            </div>
                          </div>

                          {/* H4 */}
                          <div className="flex w-full items-start gap-2.5">
                            <h3 className="font-helvetica-neue text-start text-2xl leading-8 font-medium text-[#0F172A]">
                              {store.name} Dispensary
                            </h3>
                          </div>
                        </div>

                        {/* Location & Hours */}
                        <div className="flex w-full flex-col items-start gap-3">
                          {/* Address */}
                          <div className="flex w-full items-start gap-3 pl-0.5">
                            <img
                              src="/locations/Map.svg"
                              alt="Store location"
                              className="h-6 w-[18px]"
                            />
                            <span className="font-helvetica-neue flex-1 text-lg leading-[22px] font-normal text-[#4A5565]">
                              {store.address}
                            </span>
                          </div>

                          {/* Hours */}
                          <div className="flex w-full items-center gap-3 pl-0.5">
                            <img
                              src="/locations/Clock.svg"
                              alt="Store hours"
                              className="h-[18px] w-[18px]"
                            />
                            <span className="font-helvetica-neue text-lg leading-[22px] font-normal text-[#4A5565]">
                              {store.hours.replace('Open ', '')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Buttons - Always at bottom */}
                      <div className="flex w-full items-start justify-center gap-4">
                        <Link
                          to={store.url}
                          onClick={onNavigate}
                          aria-label={`Shop Now at ${store.name}`}
                          className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] bg-[#264B9A] px-6 py-3 transition-colors hover:bg-blue-700"
                        >
                          <span className="font-helvetica-neue text-center text-base leading-4 font-medium text-nowrap text-white">
                            Shop Now
                          </span>
                        </Link>
                        <a
                          href={`tel:${store.phone || '(925) 276-9420'}`}
                          className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] px-6 py-3"
                        >
                          <img src="/locations/phone.svg" alt="Phone contact" className="h-4 w-4" />
                          <span className="font-helvetica-neue text-center text-base leading-4 font-medium text-nowrap text-[#264B9A]">
                            {store.phone || '(925) 276-9420'}
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>
      </div>
    </section>
  );
}
